import {
  roundOffAndFormatNumberWithCurrency,
  fixedDigitsAndFormatNumberWithCurrency,
} from '../../../../utils/utils'

const COLOR_MODES = {
  dark: { gain: 'text-[#00661D]', loss: 'text-[#992E00]', zero: 'text-[#232323]' },
  // light tokens darkened for WCAG 1.4.3 (View Withdrawals_B01; also used on plan cards)
  light: { gain: 'text-[#0B6B2A]', loss: 'text-[#992E00]', zero: 'text-[#232323]' },
} as const

const BAR_MODES = {
  dark: { gain: 'bg-[#00661D]', loss: 'bg-[#992E00]', zero: 'bg-[#232323]' },
  light: { gain: 'bg-[#0B6B2A]', loss: 'bg-[#992E00]', zero: 'bg-[#232323]' },
} as const

export type StyledAmountColorVariant = keyof typeof COLOR_MODES

function amountColorClass(amount: number, colorVariant: StyledAmountColorVariant): string {
  const colors = COLOR_MODES[colorVariant]
  if (amount > 0) return colors.gain
  if (amount < 0) return colors.loss
  return colors.zero
}

function amountBarClass(amount: number, colorVariant: StyledAmountColorVariant): string {
  const colors = BAR_MODES[colorVariant]
  if (amount > 0) return colors.gain
  if (amount < 0) return colors.loss
  return colors.zero
}

function amountSign(amount: number): string {
  if (amount > 0) return '+'
  if (amount < 0) return '-'
  return ''
}

type StyledAmountProps = {
  amount?: number
  className?: string
  showBar?: boolean
  label?: string
  decimals?: number
  colorVariant?: StyledAmountColorVariant
  showSign?: boolean
}

/**
 * StyledAmount — Renders a monetary amount with sign, colour and optional bar.
 *
 * Use this whenever a gain/loss/zero amount must be shown with consistent styling
 * (green / orange / grey) and optional +/− sign across tax-harvesting (e.g. cards,
 * summary rows, capital gains lines).
 *
 * Props:
 * - amount (number) — The value to display. Sign and colour are derived from it.
 * - colorVariant — 'dark' (default): #00661D / #992E00. 'light': #0B6B2A / #992E00 (4.5:1 on white).
 * - showSign — true (default): prefix with + or −. false: no sign (e.g. capital gains line).
 * - showBar — true: show a coloured vertical bar before the text (e.g. LTCG/STCG lines).
 * - label — Optional suffix after the amount (e.g. " LTCG").
 * - decimals — If set, formats with fixed decimals; otherwise uses rounded currency format.
 * - className — Extra Tailwind classes (e.g. text-right, text-[14px] for summary card).
 *
 * Default typography: text-[13px] font-[500]. Override with className where needed (e.g. summary: text-[14px]).
 *
 * Examples:
 *   <StyledAmount amount={totalGains} />
 *   <StyledAmount amount={ltcg} showBar label="LTCG" decimals={2} colorVariant="light" showSign={false} />
 *   <StyledAmount amount={netLtcg} colorVariant="dark" showSign={false} className="text-[14px] text-right" />
 */

export function StyledAmount({
  amount,
  className = '',
  showBar = false,
  label = '',
  decimals,
  colorVariant = 'dark',
  showSign = true,
}: StyledAmountProps) {
  if (amount == null) {
    return <span className={`text-[13px] font-[500] text-[#232323] ${className}`.trim()}>—</span>
  }
  const colorClass = amountColorClass(amount, colorVariant)
  const sign = showSign ? amountSign(amount) : ''
  const formatted =
    decimals != null
      ? fixedDigitsAndFormatNumberWithCurrency(Math.abs(amount), decimals)
      : roundOffAndFormatNumberWithCurrency(Math.abs(amount))
  const text = `${sign} ${formatted}${label ? ` ${label}` : ''}`

  if (showBar) {
    return (
      <div
        className={`flex items-center gap-[7px] text-[13px] font-[500] ${colorClass} ${className}`.trim()}
      >
        <span
          className={`w-[3px] h-[16px] opacity-30 shrink-0 rounded-full ${amountBarClass(amount, colorVariant)}`}
        />
        {text}
      </div>
    )
  }
  return (
    <div className={`text-[13px] font-[500] ${colorClass} ${className}`.trim()}>
      {text}
    </div>
  )
}
